import {useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useNavigate, useParams} from 'react-router-dom'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {useToast} from '@/hooks/use-toast'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {PageLayout} from '@/components/shared/PageLayout'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {Card, CardContent, CardHeader, CardTitle, CardDescription} from '@/components/ui/card'
import type {ReactNode} from 'react'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import {CONCEPT_ICON} from '@/lib/concepts'
import {DatePicker} from '@/components/shared/DatePicker'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {ErrorText} from '@/components/shared/ErrorText'
import {MutedText} from '@/components/shared/MutedText'
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {AppLocalDate, formatDateOnly, formatTimestamp} from '@/lib/date-utils'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {
    RehireReviewDecision,
    RehireReviewStatus,
    RehireDecisionActorType,
    type RehireReviewDecideRequest,
    type PreviousAccessProfile,
} from '@/types/rehire-review'
import type {Identity} from '@/types/identity'
import {PeerRecommendationList} from '@/components/shared/PeerRecommendationList'
import type {PeerAccessProfile, AccessRequestCreateRequest} from '@/types/access-request'
import {EntityType} from '@/types/entity-link'

 
const QK_REHIRE_REVIEW = 'rehire-review'
 
const QK_DETAIL = 'detail'
 
const QK_IDENTITY = 'identity'
 
const QK_REHIRE_REVIEW_COUNT = 'rehire-review-count'

const STATUS_VARIANT: Record<RehireReviewStatus, BadgeVariant> = {
    PENDING: BadgeVariant.OUTLINE,
    RESURRECTED: BadgeVariant.SUCCESS,
    CREATED_NEW: BadgeVariant.SECONDARY,
    REJECTED: BadgeVariant.DESTRUCTIVE,
}

type DialogKind = 'RESURRECT' | 'CREATE_NEW' | 'REJECT' | null

function stringifyValue(value: unknown): string {
    if (value === null || value === undefined) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

export function RehireReviewDetail() {
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const {toast} = useToast()
    const {hasPermission} = useAuth()
    const {id = ''} = useParams<{id: string}>()

    const canManage = hasPermission(permissions.rehireReview.manage)
    const canListMine = hasPermission(permissions.rehireReview.listMine)
    const canCreateAccessRequest = hasPermission(permissions.accessRequests.create)

    const [dialogKind, setDialogKind] = useState<DialogKind>(null)
    const [reason, setReason] = useState('')
    const [reasonError, setReasonError] = useState<string | null>(null)
    const [entryDate, setEntryDate] = useState('')
    const [entryDateError, setEntryDateError] = useState<string | null>(null)
    const [selectedPreviousAccess, setSelectedPreviousAccess] = useState<PreviousAccessProfile[]>([])
    const [creatingRequests, setCreatingRequests] = useState(false)

    const {data: candidate, isLoading, error} = useQuery({
        queryKey: [QK_REHIRE_REVIEW, QK_DETAIL, id],
        queryFn: () => services.rehireReview.detail(id),
        enabled: !!id,
    })

    const matchedIdentityId = candidate?.matchedIdentityId ?? ''
    const {data: matchedIdentity} = useQuery<Identity>({
        queryKey: [QK_IDENTITY, matchedIdentityId],
        queryFn: () => services.identities.detail(matchedIdentityId),
        enabled: !!matchedIdentityId,
    })

    const {data: previousAccess} = useQuery<PreviousAccessProfile[]>({
        queryKey: [QK_REHIRE_REVIEW, 'previous-access', id],
        queryFn: () => services.rehireReview.previousAccess(id),
        enabled: !!id && canListMine,
    })

    const decideMutation = useEntityMutation({
        mutationFn: (req: RehireReviewDecideRequest) => services.rehireReview.decide(id, req),
        invalidate: [[QK_REHIRE_REVIEW], [QK_REHIRE_REVIEW_COUNT]],
        onSuccess: (resp, variables) => {
            const linkId = resp.newIdentityId ?? resp.matchedIdentityId
            if (variables.decision === RehireReviewDecision.RESURRECTED) {
                toast({
                    title: i18n.rehireReview.messages.resurrectSuccess,
                    action: linkId ? (
                        <Button
                            variant="link"
                            size="sm"
                            data-testid="toast-open-identity"
                            onClick={() => navigate(routes.identityManagement.identities.detail(linkId))}
                        >
                            {i18n.rehireReview.messages.resurrectSuccessLink}
                        </Button>
                    ) : undefined,
                })
            } else if (variables.decision === RehireReviewDecision.CREATED_NEW) {
                toast({
                    title: i18n.rehireReview.messages.createNewSuccess,
                    action: resp.newIdentityId ? (
                        <Button
                            variant="link"
                            size="sm"
                            data-testid="toast-open-new-identity"
                            onClick={() => navigate(routes.identityManagement.identities.detail(resp.newIdentityId!))}
                        >
                            {i18n.rehireReview.messages.createNewSuccessLink}
                        </Button>
                    ) : undefined,
                })
            } else {
                toast({title: i18n.rehireReview.messages.rejectSuccess})
            }
            setDialogKind(null)
            setReason('')
            setReasonError(null)
            setEntryDate('')
            setEntryDateError(null)
            navigate(routes.my.rehires.list())
        },
    })

    const incomingEntries = useMemo(
        () => Object.entries(candidate?.incomingRecordSnapshot ?? {}).sort(([a], [b]) => a.localeCompare(b)),
        [candidate],
    )

    const diffRows = useMemo(() => {
        if (!matchedIdentity) return [] as {attribute: string; oldValue: string; newValue: string}[]
        const rows: {attribute: string; oldValue: string; newValue: string}[] = []
        const matchedRecord = matchedIdentity as unknown as Record<string, unknown>
        for (const [key, newVal] of incomingEntries) {
            const oldVal = matchedRecord[key]
            const oldStr = stringifyValue(oldVal)
            const newStr = stringifyValue(newVal)
            if (oldStr !== newStr) {
                rows.push({attribute: key, oldValue: oldStr, newValue: newStr})
            }
        }
        return rows
    }, [matchedIdentity, incomingEntries])

    const renderStatusBadge = (status: RehireReviewStatus) => {
        const label =
            status === RehireReviewStatus.PENDING ? i18n.rehireReview.status.pending :
            status === RehireReviewStatus.RESURRECTED ? i18n.rehireReview.status.resurrected :
            status === RehireReviewStatus.CREATED_NEW ? i18n.rehireReview.status.createdNew :
            i18n.rehireReview.status.rejected
        return <Badge variant={STATUS_VARIANT[status]}>{label}</Badge>
    }

    const openDialog = (kind: DialogKind) => {
        setDialogKind(kind)
        setReason('')
        setReasonError(null)
        if (kind === 'RESURRECT' || kind === 'CREATE_NEW') {
            setEntryDate(candidate?.newEntryDate ?? AppLocalDate.today())
        } else {
            setEntryDate('')
        }
        setEntryDateError(null)
    }

    const submitDialog = () => {
        const trimmed = reason.trim()
        if (!trimmed) {
            setReasonError(i18n.rehireReview.dialogs.reasonRequired)
            return
        }
        if (!dialogKind) return
        const decision: RehireReviewDecision =
            dialogKind === 'RESURRECT' ? RehireReviewDecision.RESURRECTED :
            dialogKind === 'CREATE_NEW' ? RehireReviewDecision.CREATED_NEW :
            RehireReviewDecision.REJECTED
        if (decision !== RehireReviewDecision.REJECTED) {
            if (!entryDate) {
                setEntryDateError(i18n.rehireReview.dialogs.entryDateRequired)
                return
            }
            const today = AppLocalDate.today()
            const maxAllowed = AppLocalDate.yearsFromToday(2)
            if (entryDate < today || entryDate > maxAllowed) {
                setEntryDateError(i18n.rehireReview.dialogs.entryDateInvalid)
                return
            }
        }
        decideMutation.mutate({
            decision,
            reason: trimmed,
            newEntryDate: decision === RehireReviewDecision.REJECTED ? null : entryDate,
        })
    }

    const addPreviousAccess = (peer: PeerAccessProfile) => {
        const profile: PreviousAccessProfile = {
            accessProfileId: peer.accessProfile.id,
            accessProfileName: peer.accessProfile.displayName,
            resourceName: peer.resource.displayName,
            environment: peer.environment,
            expiryRequired: peer.expiryRequired,
            maxDurationDays: peer.maxDurationDays,
            peerCount: peer.peerCount,
        }
        if (selectedPreviousAccess.some(p => p.accessProfileId === profile.accessProfileId)) return
        setSelectedPreviousAccess([...selectedPreviousAccess, profile])
    }

    const isPreviousAccessSelected = (peer: PeerAccessProfile) => {
        return selectedPreviousAccess.some(p => p.accessProfileId === peer.accessProfile.id)
    }

    const submitAccessRequests = async () => {
        if (!candidate || selectedPreviousAccess.length === 0) return
        // WHY: beneficiary is the rehired identity when one was created (RESURRECTED →
        // candidate.matchedIdentityId still applies because rehire reuses the same identity row;
        // CREATED_NEW → candidate.newIdentityId). Otherwise (no decision yet) we point at the
        // matched identity since it's the same party as a prospective new identity.
        const beneficiaryIdentityId = candidate.newIdentityId ?? candidate.matchedIdentityId
        setCreatingRequests(true)
        try {
            for (const profile of selectedPreviousAccess) {
                const req: AccessRequestCreateRequest = {
                    beneficiaryIdentityId,
                    justification: i18n.rehireReview.picker.defaultJustification({name: profile.accessProfileName}),
                    items: [{accessProfileId: profile.accessProfileId}],
                }
                await services.accessRequests.create(req)
            }
            toast({
                title: i18n.rehireReview.picker.requestsCreated({count: selectedPreviousAccess.length}),
                action: (
                    <Button
                        variant="link"
                        size="sm"
                        data-testid="toast-open-my-requests"
                        onClick={() => navigate(routes.my.requests.list())}
                    >
                        {i18n.rehireReview.picker.viewRequests}
                    </Button>
                ),
            })
            setSelectedPreviousAccess([])
            navigate(routes.my.requests.list())
        } catch (err) {
            // WHY: errors must be loud — surface a destructive toast so the manager sees the
            // failure (and the Toaster auto-attaches the error-log link via the global pattern
            // in {@file frontend/src/components/ui/toaster.tsx}). Stay on the detail page so
            // the manager can retry without losing the selection.
            const message = err instanceof Error ? err.message : i18n.rehireReview.picker.requestsFailed
            toast({
                variant: 'destructive',
                title: i18n.rehireReview.picker.requestsFailed,
                description: message,
            })
        } finally {
            setCreatingRequests(false)
        }
    }

    const isPending = candidate?.status === RehireReviewStatus.PENDING
    const canAct = canManage && isPending && !decideMutation.isPending

    const headerActions: ReactNode = canAct ? (
        <InlineRow gap="md" wrap data-testid="rehire-review-action-bar">
            <Button
                variant="default"
                data-testid="btn-resurrect"
                onClick={() => openDialog('RESURRECT')}
            >
                {i18n.rehireReview.detail.actions.resurrect}
            </Button>
            <Button
                variant="default"
                data-testid="btn-create-new"
                onClick={() => openDialog('CREATE_NEW')}
            >
                {i18n.rehireReview.detail.actions.createNew}
            </Button>
            <Button
                variant="destructive"
                data-testid="btn-reject"
                onClick={() => openDialog('REJECT')}
            >
                {i18n.rehireReview.detail.actions.reject}
            </Button>
        </InlineRow>
    ) : undefined

    const dialogCopy =
        dialogKind === 'RESURRECT' ? i18n.rehireReview.dialogs.resurrect :
        dialogKind === 'CREATE_NEW' ? i18n.rehireReview.dialogs.createNew :
        dialogKind === 'REJECT' ? i18n.rehireReview.dialogs.reject :
        null

    return (
        <PageLayout
            variant="detail"
            icon={CONCEPT_ICON.rehireReview}
            title={i18n.rehireReview.detail.title}
            description={candidate ? renderStatusBadge(candidate.status) : undefined}
            actions={headerActions}
            backTo={routes.my.rehires.list()}
            loading={isLoading}
            error={error}
            data-testid="rehire-review-detail"
        >
            {candidate ? (
                <>
                    <DetailGrid columns={2}>
                        <DetailsCard
                            title={i18n.rehireReview.detail.matchedCard.title}
                            data-testid="rehire-review-matched-card"
                            fields={[
                                {
                                    label: i18n.rehireReview.detail.matchedCard.priorName,
                                    value: matchedIdentity?.derivedDisplayName ?? candidate.matchedIdentityDisplayName ?? '',
                                },
                                {
                                    label: i18n.rehireReview.detail.matchedCard.lifecycleState,
                                    value: matchedIdentity?.lifecycleState ?? '',
                                },
                                {
                                    label: i18n.rehireReview.detail.matchedCard.entryDate,
                                    value: matchedIdentity?.entryDate ? formatDateOnly(matchedIdentity.entryDate) : '',
                                },
                                {
                                    label: i18n.rehireReview.detail.matchedCard.exitDate,
                                    value: matchedIdentity?.exitDate ? formatDateOnly(matchedIdentity.exitDate) : '',
                                },
                            ]}
                        />
                        <DetailsCard
                            title={i18n.rehireReview.detail.incomingCard.title}
                            data-testid="rehire-review-incoming-card"
                            fields={[
                                {
                                    label: i18n.rehireReview.detail.incomingCard.newEntryDate,
                                    value: candidate.newEntryDate ? formatDateOnly(candidate.newEntryDate) : '',
                                },
                                {
                                    label: i18n.rehireReview.detail.incomingCard.sourceConnector,
                                    value: candidate.sourceConnectorDisplayName ?? candidate.sourceConnectorId,
                                },
                                {
                                    label: i18n.rehireReview.detail.incomingCard.sourceSyncRun,
                                    value: candidate.sourceSyncRunId,
                                },
                                {
                                    label: i18n.rehireReview.detail.incomingCard.detectedAt,
                                    value: formatTimestamp(candidate.detectedAt),
                                },
                            ]}
                        />
                    </DetailGrid>

                    <Card data-testid="rehire-review-diff-card">
                        <CardHeader>
                            <CardTitle data-testid="card-title-rehire-review-diff">{i18n.rehireReview.detail.diff.title}</CardTitle>
                            <CardDescription>{i18n.rehireReview.detail.diff.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {diffRows.length === 0 ? (
                                <MutedText block>{i18n.rehireReview.detail.diff.noChanges}</MutedText>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead data-testid="col-attribute">{i18n.rehireReview.detail.diff.attribute}</TableHead>
                                            <TableHead data-testid="col-old-value">{i18n.rehireReview.detail.diff.oldValue}</TableHead>
                                            <TableHead data-testid="col-new-value">{i18n.rehireReview.detail.diff.newValue}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {diffRows.map(row => (
                                            <TableRow key={row.attribute} data-testid={`diff-row-${row.attribute}`}>
                                                <TableCell>{row.attribute}</TableCell>
                                                <TableCell>{row.oldValue || '—'}</TableCell>
                                                <TableCell>{row.newValue || '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>

                    {canListMine && previousAccess && previousAccess.length > 0 && (
                        <Card data-testid="rehire-review-previous-access-card">
                            <CardHeader>
                                <CardTitle data-testid="card-title-previous-access">
                                    {i18n.rehireReview.picker.title({
                                        name: matchedIdentity?.derivedDisplayName ?? candidate.matchedIdentityDisplayName ?? '',
                                    })}
                                </CardTitle>
                                <CardDescription>{i18n.rehireReview.picker.description}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Stack gap="md">
                                    <PeerRecommendationList
                                        peers={(previousAccess ?? []).map(p => ({
                                            accessProfile: {
                                                id: p.accessProfileId,
                                                displayName: p.accessProfileName,
                                                entityType: EntityType.ACCESS_PROFILE,
                                                subType: null,
                                                code: null,
                                            },
                                            resource: {
                                                id: '',
                                                displayName: p.resourceName,
                                                entityType: EntityType.RESOURCE,
                                                subType: null,
                                                code: null,
                                            },
                                            environment: p.environment,
                                            expiryRequired: p.expiryRequired,
                                            maxDurationDays: p.maxDurationDays,
                                            peerCount: p.peerCount,
                                        }))}
                                        isAlreadySelected={isPreviousAccessSelected}
                                        onAdd={addPreviousAccess}
                                    />
                                    <InlineRow gap="md" justify="between">
                                        <MutedText size="sm" data-testid="previous-access-selection-count">
                                            {i18n.rehireReview.picker.selectionCount({count: selectedPreviousAccess.length})}
                                        </MutedText>
                                        <Button
                                            variant="default"
                                            disabled={selectedPreviousAccess.length === 0 || creatingRequests || !canCreateAccessRequest}
                                            onClick={submitAccessRequests}
                                            data-testid="btn-create-access-requests"
                                        >
                                            {i18n.rehireReview.picker.createRequests}
                                        </Button>
                                    </InlineRow>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    {!canManage && (
                        <MutedText block data-testid="rehire-review-readonly">
                            {i18n.rehireReview.messages.readOnly}
                        </MutedText>
                    )}
                    {canManage && !isPending && candidate.decidedByActorId && (
                        <Stack gap="sm" data-testid="rehire-review-decision-info">
                            <MutedText block size="sm" data-testid="decision-info-decided-by">
                                {candidate.decidedByActorType === RehireDecisionActorType.CONNECTOR
                                    ? (
                                        <>
                                            {i18n.rehireReview.detail.decisionInfo.decidedBy}:{' '}
                                            <Badge
                                                variant={BadgeVariant.SECONDARY}
                                                data-testid="badge-decided-by-connector"
                                            >
                                                {i18n.rehireReview.decidedByConnector({
                                                    name: candidate.decidedByConnectorName ?? candidate.decidedByActorId,
                                                })}
                                            </Badge>
                                        </>
                                    )
                                    : `${i18n.rehireReview.detail.decisionInfo.decidedBy}: ${candidate.decidedByActorId}`}
                            </MutedText>
                            {candidate.decidedAt && (
                                <MutedText block size="sm">
                                    {i18n.rehireReview.detail.decisionInfo.decidedAt}: {formatTimestamp(candidate.decidedAt)}
                                </MutedText>
                            )}
                            {candidate.decisionComment && (
                                <MutedText block size="sm">
                                    {i18n.rehireReview.detail.decisionInfo.comment}: {candidate.decisionComment}
                                </MutedText>
                            )}
                        </Stack>
                    )}
                </>
            ) : null}

            <Dialog open={dialogKind !== null} onOpenChange={open => !open && setDialogKind(null)}>
                <DialogContent data-testid="rehire-review-decision-dialog">
                    {dialogCopy && (
                        <>
                            <DialogHeader>
                                <DialogTitle>{dialogCopy.title}</DialogTitle>
                                <DialogDescription>{dialogCopy.description}</DialogDescription>
                            </DialogHeader>
                            <Stack gap="sm">
                                {dialogKind !== 'REJECT' && (
                                    <>
                                        <Label htmlFor="rehire-entry-date">
                                            {i18n.rehireReview.dialogs.entryDateLabel}
                                        </Label>
                                        <DatePicker
                                            testId="input-new-entry-date"
                                            value={entryDate}
                                            onChange={(iso) => {
                                                setEntryDate(iso)
                                                if (entryDateError) setEntryDateError(null)
                                            }}
                                            ariaLabel={String(i18n.rehireReview.dialogs.entryDateLabel)}
                                        />
                                        <MutedText size="sm">
                                            {i18n.rehireReview.dialogs.entryDateHint}
                                        </MutedText>
                                        {entryDateError && (
                                            <ErrorText data-testid="entry-date-error">{entryDateError}</ErrorText>
                                        )}
                                    </>
                                )}
                                <Label htmlFor="rehire-reason">{dialogCopy.reasonLabel}</Label>
                                <Textarea
                                    id="rehire-reason"
                                    data-testid="input-reason"
                                    value={reason}
                                    placeholder={dialogCopy.reasonPlaceholder}
                                    onChange={e => {
                                        setReason(e.target.value)
                                        if (reasonError) setReasonError(null)
                                    }}
                                />
                                {reasonError && (
                                    <ErrorText data-testid="reason-error">{reasonError}</ErrorText>
                                )}
                            </Stack>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => setDialogKind(null)}
                                    data-testid="btn-cancel"
                                >
                                    {i18n.common.cancel}
                                </Button>
                                <Button
                                    variant={dialogKind === 'REJECT' ? 'destructive' : 'default'}
                                    onClick={submitDialog}
                                    disabled={decideMutation.isPending}
                                    data-testid="btn-submit"
                                >
                                    {dialogCopy.submit}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </PageLayout>
    )
}
